import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import config from "constants/config";
import { SOCKET_EVENTS, SOCKET_LISTENERS } from "constants/global";

/**
 * Hook to manage real-time WebSocket connection for business account opening fee updates.
 *
 * Connects to the root Socket.io endpoint (matching Postman {{localURL}} spec),
 * subscribes using `subscribeOpeningFeeStatus` with { businessId }, and listens
 * for webhook-driven `openingFeeStatusUpdated` events.
 *
 * @param {Object} params
 * @param {string} params.businessId - The ID of the current business.
 * @param {boolean} [params.enabled=true] - Whether the socket should be active.
 * @param {Function} [params.onStatusUpdated] - Callback fired when `openingFeeStatusUpdated` event is received.
 * @returns {{ socket: any, isConnected: boolean, subscribeOpeningFeeStatus: Function }}
 */
const useOpeningFeeSocket = ({
  businessId,
  enabled = true,
  onStatusUpdated,
} = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const onStatusUpdatedRef = useRef(onStatusUpdated);
  const pendingSubscribeRef = useRef(null);

  // Keep latest callback reference without triggering reconnects
  useEffect(() => {
    onStatusUpdatedRef.current = onStatusUpdated;
  }, [onStatusUpdated]);

  const subscribeOpeningFeeStatus = useCallback((customPayload) => {
    const payload = customPayload || (businessId ? { businessId } : null);
    if (!payload) return;

    if (socketRef.current?.connected) {
      socketRef.current.emit(SOCKET_EVENTS.SUBSCRIBE_OPENING_FEE_STATUS, payload);
    } else {
      pendingSubscribeRef.current = payload;
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      }
    }
  }, [businessId]);

  useEffect(() => {
    if (!businessId || !enabled) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected((prev) => (prev ? false : prev));
      }
      return;
    }

    // Connect to the root Socket.io server matching {{localURL}}
    const socket = io(config.API_URL, {
      extraHeaders: {
        "x-business-id": businessId,
      },
      autoConnect: true,
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    const onConnect = () => {
      setIsConnected((prev) => (!prev ? true : prev));
      if (pendingSubscribeRef.current) {
        socket.emit(SOCKET_EVENTS.SUBSCRIBE_OPENING_FEE_STATUS, pendingSubscribeRef.current);
        pendingSubscribeRef.current = null;
      }
    };

    const onDisconnect = () => {
      setIsConnected((prev) => (prev ? false : prev));
    };

    const onConnectError = (error) => {
      console.error("Opening fee socket connection error:", error);
      setIsConnected((prev) => (prev ? false : prev));
    };

    const handleOpeningFeeStatusUpdated = (data) => {
      if (onStatusUpdatedRef.current) {
        onStatusUpdatedRef.current(data);
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on(
      SOCKET_LISTENERS.OPENING_FEE_STATUS_UPDATED,
      handleOpeningFeeStatusUpdated
    );

    // If socket is already connected immediately
    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off(
        SOCKET_LISTENERS.OPENING_FEE_STATUS_UPDATED,
        handleOpeningFeeStatusUpdated
      );
      socket.disconnect();
      socketRef.current = null;
      setIsConnected((prev) => (prev ? false : prev));
    };
  }, [businessId, enabled]);

  return {
    socket: socketRef.current,
    isConnected,
    subscribeOpeningFeeStatus,
  };
};

export default useOpeningFeeSocket;
